/**
 * The provider marks, at the size a sign-in button puts them.
 *
 * These are the vendors' own logos — Google's four-colour G, Microsoft's four-square window,
 * Zoho's four overlapping squares — drawn from the assets the vendors publish rather than
 * redrawn or approximated. That is the point of them: a provider button is the one place on
 * the page where the identity is not ours, and a hand-drawn likeness of somebody else's mark
 * would be both a trademark problem and a worse-looking thing.
 *
 * ## Why the colours here are literal, and why that is not a palette violation
 *
 * Every other surface in this project names tokens. These files do not, and must not: the
 * fill values *are* the brand. Re-binding `#4285F4` to `--accent` would make the Google mark
 * wrong, and it would do it on the screen where a person judges whether the button is real
 * before handing over a work address. This is the same exception `editor/state.ts` holds for
 * email content, and `.impeccable/config.json` excludes this file from the design detector for
 * the same reason.
 *
 * ## Sizing
 *
 * Each mark has an intrinsic size derived from its own aspect ratio — Google and Microsoft are
 * square at 16px, Zoho is 1009.5 × 345 and so is 53 × 18 at the same optical weight. The size
 * is applied as an inline style rather than a class because `Button`'s own icon rule
 * (`[&_svg:not([class*='size-'])]:size-4`) would otherwise clamp every mark to a 16px square,
 * which turns the wide Zoho mark into a 16 × 5.5 sliver. A brand mark has a fixed intrinsic
 * size; this makes that explicit and immune to the button it sits in.
 *
 * Every mark is decorative and `aria-hidden`, because the button's own label — visible and
 * accessible — already names the provider. Announcing "Google" twice, once as an unlabelled
 * graphic, is noise.
 */

/** Google's four-colour G. View box as published; square. */
function GoogleMark() {
	return (
		<svg viewBox="0 0 48 48" role="presentation" aria-hidden="true" focusable="false" style={{ width: 16, height: 16 }}>
			<path
				fill="#EA4335"
				d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
			/>
			<path
				fill="#4285F4"
				d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
			/>
			<path
				fill="#FBBC05"
				d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
			/>
			<path
				fill="#34A853"
				d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
			/>
		</svg>
	)
}

/** Microsoft's four-square window: red, green, blue, yellow, with the published gap. */
function MicrosoftMark() {
	return (
		<svg viewBox="0 0 24 24" role="presentation" aria-hidden="true" focusable="false" style={{ width: 16, height: 16 }}>
			<rect x="0" y="0" width="11.408" height="11.408" fill="#F25022" />
			<rect x="12.594" y="0" width="11.408" height="11.408" fill="#7FBA00" />
			<rect x="0" y="12.594" width="11.408" height="11.408" fill="#00A4EF" />
			<rect x="12.594" y="12.594" width="11.408" height="11.408" fill="#FFB900" />
		</svg>
	)
}

/**
 * Zoho's four overlapping squares, cropped to the mark.
 *
 * The published asset is the full lockup — the mark above the ZOHO wordmark — at 1024 × 450.
 * The wordmark is dropped here because the button's own label already says "Zoho", and the
 * view box is the measured union of the four coloured paths (7.3, 8.1 → 1016.8, 353.1) rather
 * than the lockup's box, so the mark fills its space instead of sitting in the top half of it
 * with a band of empty wordmark beneath.
 */
function ZohoMark() {
	return (
		<svg viewBox="7.3 8.1 1009.5 345" role="presentation" aria-hidden="true" focusable="false" style={{ width: 53, height: 18 }}>
			<path
				fill="#089949"
				d="m458.1 353c-7.7 0-15.5-1.6-23-4.9l-160-71.3c-28.6-12.7-41.5-46.4-28.8-75l71.3-160c12.7-28.6 46.4-41.5 75-28.8l160 71.3c28.6 12.7 41.5 46.4 28.8 75l-71.3 160c-9.5 21.2-30.3 33.7-52 33.7zm-9.7-34.9c12.1 5.4 26.3-0.1 31.7-12.1l71.3-160c5.4-12.1-0.1-26.3-12.1-31.7l-160.1-71.3c-12.1-5.4-26.3 0.1-31.7 12.1l-71.3 160c-5.4 12.1 0.1 26.3 12.1 31.7z"
			/>
			<path
				fill="#f9b21d"
				d="m960 353.1h-175.2c-31.3 0-56.8-25.5-56.8-56.8v-175.2c0-31.3 25.5-56.8 56.8-56.8h175.2c31.3 0 56.8 25.5 56.8 56.8v175.2c0 31.3-25.5 56.8-56.8 56.8zm-175.2-256c-13.2 0-24 10.8-24 24v175.2c0 13.2 10.8 24 24 24h175.2c13.2 0 24-10.8 24-24v-175.2c0-13.2-10.8-24-24-24z"
			/>
			<path
				fill="#e42527"
				d="m303.9 153.2-23.6 52.8c-0.3 0.6-0.6 1.1-0.9 1.6l9.2 56.8c2.1 13.1-6.8 25.4-19.8 27.5l-173 28c-6.3 1-12.7-0.5-17.9-4.2s-8.6-9.3-9.6-15.6l-28-173c-1-6.3 0.5-12.7 4.2-17.9s9.3-8.6 15.6-9.6l173-28c1.3-0.2 2.6-0.3 3.8-0.3 11.5 0 21.8 8.4 23.7 20.2l9.3 57.2 24.4-54.7-1.3-7.7c-5-30.9-34.2-52-65.1-47l-173 28c-14.9 2.3-28.1 10.4-36.9 22.7-8.9 12.3-12.4 27.3-10 42.3l28 173c2.4 15 10.5 28.1 22.8 37 9.7 7.1 21.2 10.7 33.1 10.7 3 0 6.1-0.2 9.2-0.7l173-28c30.9-5 52-34.2 47-65.1z"
			/>
			<path
				fill="#226db4"
				d="m511.4 235.8 25.4-56.9-7.2-52.9c-0.9-6.3 0.8-12.6 4.7-17.7s9.5-8.4 15.9-9.2l173.6-23.6c1.1-0.1 2.2-0.2 3.3-0.2 5.2 0 10.2 1.7 14.5 4.9 0.8 0.6 1.5 1.3 2.2 1.9 7.7-8.1 17.8-13.9 29.1-16.4-3.2-4.4-7-8.3-11.5-11.7-12.1-9.2-27-13.1-42-11.1l-173.8 23.6c-15 2-28.4 9.8-37.5 21.9-9.2 12.1-13.1 27-11.1 42z"
			/>
			<path
				fill="#226db4"
				d="m806.8 265.1-22.8-168c-12.8 0.4-23.1 11-23.1 23.9v49.3l13.5 99.2c0.9 6.3-0.8 12.6-4.7 17.7s-9.5 8.4-15.9 9.2l-173.6 23.6c-6.3 0.9-12.6-0.8-17.7-4.7s-8.4-9.5-9.2-15.9l-8-58.9-25.4 56.9 0.9 6.4c2 15 9.8 28.4 21.9 37.5 10 7.6 21.9 11.6 34.3 11.6 2.6 0 5.2-0.2 7.8-0.5l173.4-23.4c15-2 28.4-9.8 37.5-21.9 9.2-12.1 13.1-27 11.1-42z"
			/>
		</svg>
	)
}

/**
 * The mark for a provider id, or nothing.
 *
 * `null` is the honest answer for an id this build does not know: `/v1/auth-methods` reports
 * what a deployment holds, and a deployment that adds a provider this file has no mark for
 * should get a button with its name and no logo, not a wrong logo.
 */
export function ProviderMark({ provider }: { provider: string }) {
	switch (provider) {
		case 'google':
			return <GoogleMark />
		case 'microsoft':
			return <MicrosoftMark />
		case 'zoho':
			return <ZohoMark />
		default:
			return null
	}
}
